




















import Vue from 'vue'
import {
  Control,
  LatLngBounds,
  LayerGroup,
  LeafletEvent,
  LeafletMouseEvent,
  Map,
  Marker as LeafletMarker
} from 'leaflet'
import { addButton, loadLeaflet } from './leaflet'
import { ClusterMarker, CustomMarker } from '~/models/search/map/types'
import { Classified } from '~/models/classified/types'
import { faCheck, faSearch } from '@fortawesome/free-solid-svg-icons'
import { toCamelCase } from '~/utils/object'

interface Data {
  map: Map
  L: LayerGroup
  layerGroup: LayerGroup
  hoverMarkerLayer: LayerGroup
  zoom: number
  defaultMapView: Array<number>
  isMapMoved: boolean
  controlButtons: []
  hasMapInitialized: boolean
  icons: object
  preventMoveend: boolean
  allowToEmitMoveStart: boolean
}
export default Vue.extend({
  props: {
    mapId: { type: String, default: 'map' },
    // radius: { type: Number, required: false, default: 500 },
    geolocation: { type: Array, default: () => [] },
    markers: { type: Array, default: () => [] },
    clusters: { type: Array, default: () => [] },
    maxBounds: { type: Object, default: null },
    legendData: { type: Array, default: null },
    buttons: { type: Array, default: () => [] },
    hoveredClassified: { type: Object, default: null },
    hideZoomControls: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    tap: { type: Boolean, default: false },
    minZoom: { type: Number, default: 7 },
    disableLegendEvents: { type: Boolean, default: true }
  },

  data(): Data {
    return {
      L: null,
      layerGroup: null,
      hoverMarkerLayer: null,
      map: null,
      zoom: this.minZoom,
      defaultMapView: [38.25, 23.75],
      isMapMoved: false,
      // isListFrozen: false,
      controlButtons: [],
      hasMapInitialized: false,
      icons: { check: faCheck, searchIcon: faSearch },
      preventMoveend: false,
      allowToEmitMoveStart: false
    }
  },

  computed: {
    maxBoundsLatLng() {
      return this.L.latLngBounds(
        this.L.latLng(this.maxBounds.lat.max, this.maxBounds.lon.max),
        this.L.latLng(this.maxBounds.lat.min, this.maxBounds.lon.min)
      )
    },
    legend(): Control {
      const legend: Control = this.L.control({
        position: 'bottomleft'
      })
      legend.onAdd = () => {
        const div = this.L.DomUtil.create('div', 'legend-info')
        const data = this.legendData.map(l => {
          return `<span data-exact-location="${l.exactLocation}">
              <div class="color" style="background:${l.color}"></div>
              <div class="text ${
                this.disableLegendEvents ? '' : 'cursor-pointer'
              }">${l.text}</div>
            </span>
            `
        })

        div.innerHTML = data.join('')
        div.onclick = this.handleShowExactLocation
        return div
      }

      return legend
    },
    popupLoader() {
      return this.removeDisplayNone(this.$refs.loader.outerHTML)
    },
    checkIcon() {
      return this.removeDisplayNone(this.$refs.checkIcon.outerHTML)
    }
  },
  watch: {
    clusters() {
      if (this.layerGroup?.clearLayers) {
        this.layerGroup.clearLayers()
        this.placeClusters()
      }
    },
    markers() {
      if (this.hasMapInitialized) {
        this.placeMarkers()
      }
    },
    hoveredClassified(classified: Classified) {
      if (this.hasMapInitialized) {
        this.placeHoveredClassifiedMarker(classified)
      }
    },
    geolocation() {
      if (this.hasMapInitialized) {
        this.preventMoveend = true
        this.fitToBounds(undefined, this.map)
      }
    },
    buttons() {
      if (this.hasMapInitialized) {
        this.removeAllControlButtons()
        this.placeButtons()
      }
    }
  },
  async mounted() {
    this.L = await loadLeaflet()
    this.initializeMap()
    this.placeButtons()
    this.placeHoveredClassifiedMarker(this.hoveredClassified)
  },
  methods: {
    handleShowExactLocation(e: Event) {
      if (this.disableLegendEvents) {
        return
      }
      const target = e.target as HTMLElement
      const showExactLocation =
        target.parentElement.getAttribute('data-exact-location') === 'true'
      this.$emit('show-exact-location', showExactLocation)
    },
    invalidateSize() {
      this.map && this.map.invalidateSize()
    },
    initListeners() {
      this.map.on('movestart', () => {
        if (this.allowToEmitMoveStart) {
          this.$emit('move-start')
          this.allowToEmitMoveStart = false
        }
      })
      this.map.on('moveend', () => {
        this.allowToEmitMoveStart = true
        if (this.preventMoveend) {
          this.preventMoveend = false
          return
        }
        this.zoom = this.map.getZoom()
        this.isMapMoved = true

        this.onBoundsChange()
        this.$emit('map-moved')
      })
    },
    placeButtons() {
      if (this.buttons?.length) {
        // const L = this.L
        this.buttons.forEach(button => {
          const buttonControl = addButton({ L: this.L, map: this.map }, button)
          this.controlButtons.push(buttonControl)
        })
      }
    },
    removeAllControlButtons() {
      this.controlButtons.forEach(bc => bc.remove())
      this.controlButtons = []
    },
    initializeMap() {
      this.L.Control.include({
        _refocusOnMap: this.L.Util.falseFn
      })
      const tiles = this.L.tileLayer(
        'https://map.pkw.gr/osm_tiles/{z}/{x}/{y}.png',
        {
          attribution:
            '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
          minZoom: this.minZoom
        }
      )
      const map = this.L.map(this.mapId, {
        preferCanvas: true,
        attributionControl: false,
        layers: [tiles],
        maxBoundsViscosity: 1.0,
        zoomControl: !this.hideZoomControls,
        // scrollWheelZoom: this.scrollWheelZoomEnabled,
        // dragging: this.draggingEnabled,
        tap: this.tap,
        minZoom: this.minZoom,
        isMapMoved: false
      })
      const layerGroup = this.L.layerGroup()
      const hoverMarkerLayer = this.L.layerGroup()
      layerGroup.addTo(map)
      hoverMarkerLayer.addTo(map)
      if (this.maxBounds) {
        map.setMaxBounds(this.maxBoundsLatLng)
      }
      if (this.geolocation) {
        this.fitToBounds(undefined, map)
      } else {
        map.setView(this.defaultMapView, this.minZoom)
      }

      if (this.legendData) {
        this.legend.addTo(map)
      }
      map.whenReady(() => {
        this.map = map
        this.layerGroup = layerGroup
        this.hoverMarkerLayer = hoverMarkerLayer

        this.$nextTick(() => {
          this.placeClusters()
          this.placeMarkers()
          this.initListeners()
          this.invalidateSize()
          this.hasMapInitialized = true
          this.$emit('initialized')
        })
      })
      this.allowToEmitMoveStart = true
    },
    clusterClasses(numberOfMakrers: number): string {
      if (numberOfMakrers <= 11) {
        return 'sm'
      } else if (numberOfMakrers > 10 && numberOfMakrers <= 100) {
        return 'md'
      } else if (numberOfMakrers > 100 && numberOfMakrers <= 9999) {
        return 'lg'
      } else if (numberOfMakrers > 9999 && numberOfMakrers < 999999) {
        return 'xl'
      } else {
        return 'xl over-milion'
      }
    },

    placeClusters() {
      this.clusters.forEach((c: ClusterMarker) => {
        this.L.marker(c.rect[0], {
          icon: new this.L.DivIcon({
            html: `<div><span> ${c.count} </span></div>`,
            className: `cluster-marker ${this.clusterClasses(c.count)}`,
            iconSize: new this.L.Point(40, 40)
          })
        })
          .addTo(this.layerGroup)
          .on('click', _ => {
            this.onClusterClick(c)
          })
      })
    },
    placeMarkers() {
      this.markers.forEach((m: CustomMarker, i: number) => {
        this.createMarker(m, i)
      })
    },
    createMarker(
      marker: CustomMarker,
      index: number,
      layer: LayerGroup = this.layerGroup,
      isSelected: boolean = false
    ) {
      const price = this.normalizePrice(marker.price)
      const leafletMarker = this.L.marker(marker.geolocation, {
        icon: new this.L.DivIcon({
          html: `<div class="px-1"> ${price}</div>
          <span class="${marker.seenByUser ? 'd-block' : 'd-none'}">
          ${this.checkIcon}
          </span>
          `,
          className: `marker ${marker.exactLocation ? 'exact' : 'n-exact'} ${
            isSelected ? 'selected' : ''
          } ${this.hoveredClassified ? 'hovered' : ''}`,
          iconSize: new this.L.Point(71, 23)
          // iconSize: new this.L.Point(40, 40)
        }),
        zIndexOffset: index + 1000
      })
        .addTo(layer)
        .on('click', (e: LeafletMouseEvent) => {
          e.originalEvent.stopPropagation()

          this.onMarkerClick(e, marker, leafletMarker)
        })
    },
    normalizePrice(price): string {
      let priceWithoutCurrency = price
      if (typeof priceWithoutCurrency === 'string') {
        priceWithoutCurrency = priceWithoutCurrency.replace('€', '').trim()
      }
      if (priceWithoutCurrency && typeof priceWithoutCurrency === 'object') {
        return `${this.addDotsInPrice(
          priceWithoutCurrency.from
        )} € -  ${this.addDotsInPrice(priceWithoutCurrency.to)} €`
      } else if (
        !isNaN(parseFloat(priceWithoutCurrency)) &&
        priceWithoutCurrency !== 0
      ) {
        return `${this.addDotsInPrice(priceWithoutCurrency)} €`
      }
      return this.$i18n.t('ask price')
    },
    addDotsInPrice(price: string): string {
      if (price) {
        return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
      }
      return ''
    },
    flyTo(latLon: [], zoom: number = 16, updateBounce: boolean = false) {
      this.map.flyTo(latLon, zoom, { animate: false })
      this.allowToEmitMoveStart = false
      if (updateBounce) {
        this.onBoundsChange()
      }
    },
    onClusterClick(cluster: ClusterMarker) {
      if (this.map.getZoom() === this.map.getMaxZoom() && cluster.count > 1) {
        this.$emit('cluster-with-multiple-classifieds', cluster)
      } else {
        this.flyTo(Object.values(cluster.center), this.map.getZoom() + 2)
        this.onBoundsChange()
      }
    },
    onMarkerClick(
      e: LeafletEvent,
      marker: CustomMarker,
      leafletMarker: LeafletMarker
    ) {
      leafletMarker.getElement().classList.add('display-check')

      this.$emit('marker-click', { marker, leafletMarker, e })
    },
    classifiedToMarker(classified: Classified): CustomMarker {
      let exactLocation = false
      let geolocation = null
      let price = classified.price
      if (classified?.price?.value) {
        price = classified.price.value.replace('€', '')
      } else if (classified.price?.extra?.askPrice) {
        price = this.$i18n.t('ask price')
      }
      if (classified?.location?.exactLocation) {
        exactLocation =
          classified.location.exactLocation || classified.location.geolocation
      } else if (classified?.geolocation?.exactLocation) {
        exactLocation = classified.geolocation.exactLocation
      }

      if (classified?.location?.geolocation) {
        geolocation = classified.location.geolocation
      } else if (classified?.geolocation?.geolocation) {
        geolocation = classified.geolocation.geolocation
      }
      return {
        exactLocation,
        geolocation,
        id: classified.id,
        price,
        seenByUser: false
      }
    },
    onBoundsChange() {
      const {
        _northEast: northEast,
        _southWest: southWest
      } = this.map.getBounds()
      this.isMapMoved = false
      this.$emit('bounds-change', {
        northEast: {
          lat: northEast.lat.toFixed(9),
          lng: northEast.lng.toFixed(9)
        },
        southWest: {
          lat: southWest.lat.toFixed(9),
          lng: southWest.lng.toFixed(9)
        },
        withDebounce: true
      })
    },
    fitBoundsByLocation(arrayOfMarkers: []) {
      this.allowToEmitMoveStart = false
      this.preventMoveend = true
      const bounds = new this.L.LatLngBounds(arrayOfMarkers)
      this.map.fitBounds(bounds, undefined)
    },
    fitToBounds(
      boundsParams: LatLngBounds = null,
      map: Map = this.map,
      preventMoveend: boolean = true
    ) {
      this.allowToEmitMoveStart = false
      if (!boundsParams) {
        const [southWest, northEast] = this.geolocation
        const bounds = this.L.latLngBounds(southWest, northEast)
        map.fitBounds(bounds)
      } else {
        this.preventMoveend = preventMoveend
        map.fitBounds(boundsParams)
      }
    },
    placeHoveredClassifiedMarker(classified: Classified) {
      if (classified && classified.states) {
        const marker = this.classifiedToMarker(toCamelCase(classified))
        if (marker.geolocation) {
          this.createMarker(marker, 0, this.hoverMarkerLayer, true)
        }
      } else {
        this.hoverMarkerLayer.clearLayers()
      }
    },
    isGRVisible(): boolean {
      return this.map.getBounds().overlaps([
        [41.103380158, 25.568555535],
        [36.651225433, 28.780469598]
      ])
    },
    isCYVisible(): boolean {
      return this.map.getBounds().overlaps([
        [35.900175065, 34.711303711],
        [33.902336404, 32.099304199]
      ])
    },
    removeDisplayNone(val: string): string {
      if (val) {
        return val.replace('style="display: none;"', '')
      }
      return null
    }
  }
})
